/**
 * User UI Preferences Service
 * GET/PATCH /api/users/me/ui-preferences (theme, mode, font size)
 * Works for any authenticated user (platform, org, branch).
 */

import api from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api.constants'

export interface UIPreferencesTheme {
  id: string
  key: string
  name: string
  mode: string
  config?: Record<string, unknown>
  [key: string]: unknown
}

export interface UIPreferencesFontSize {
  id: string
  key: string
  name: string
  fontSize: string
  baseMultiplier?: number
  [key: string]: unknown
}

export interface UIPreferences {
  /** When true (default), theme follows platform default; when false, user's chosen theme is used. Omitted from older API = true. */
  usePlatformDefault?: boolean
  /** When true (default), mode follows platform default (default_theme_mode setting); when false, user's chosen mode is used. */
  usePlatformDefaultMode?: boolean
  /** Platform default mode (light/dark/system) — set at Platform > App Settings. Present when usePlatformDefaultMode is used. */
  platformDefaultMode?: string
  themeId: string | null
  theme: UIPreferencesTheme | null
  mode: string
  fontSizeId: string | null
  fontSize: UIPreferencesFontSize | null
}

export interface PatchUIPreferencesInput {
  usePlatformDefault?: boolean
  usePlatformDefaultMode?: boolean
  themeId?: string | null
  mode?: 'light' | 'dark' | 'system'
  fontSizeId?: string | null
}

export async function getUIPreferences(): Promise<UIPreferences> {
  const response = await api.get<{ success: boolean; data: UIPreferences }>(
    API_ENDPOINTS.USERS.UI_PREFERENCES
  )
  return response.data?.data ?? response.data
}

export async function patchUIPreferences(
  input: PatchUIPreferencesInput
): Promise<UIPreferences> {
  const response = await api.patch<{ success: boolean; data: UIPreferences }>(
    API_ENDPOINTS.USERS.UI_PREFERENCES,
    input
  )
  return response.data?.data ?? response.data
}

export interface UIPreferencesOptions {
  themes: UIPreferencesTheme[]
  fontSizes: UIPreferencesFontSize[]
  /** Platform default theme mode (light/dark/system) for display in appearance settings. */
  defaultThemeMode?: string
}

export async function getUIPreferencesOptions(): Promise<UIPreferencesOptions> {
  const response = await api.get<{ success: boolean; data: UIPreferencesOptions }>(
    API_ENDPOINTS.USERS.UI_PREFERENCES_OPTIONS
  )
  return response.data?.data ?? response.data
}
